/**
 * The platform's fee schedule in one place, for Admin → Fees and the CRM (GET / PUT
 * /api/crm/settings/fees). Nothing new is stored here, it is a view over the two places fees live:
 *
 *   trading     commission per side (open and close) as a fraction of notional, set per asset class
 *               onto every instrument in it (instruments.fee_rate; Admin → Instruments can still give
 *               one instrument its own rate, which shows here as a range)
 *   trading_flat  flat USD fee charged once per trade, when the position opens, set per asset class
 *               the same way (instruments.fee_flat)
 *   withdrawal  fee taken from the amount: per crypto asset in its coin (USDT, BTC, ETH), USD per
 *               payout currency for wire and card (platform_settings 'withdrawal_limits', which
 *               Admin → Withdrawal limits also edits)
 *
 * Spreads, markup and overnight swap stay per instrument in Admin → Instruments.
 */
import { WITHDRAWAL_ASSETS } from './account.js';
import { D } from './decimal.js';
import { coinAmountRe, MONEY_RE, PAYOUT_CURRENCIES } from './payouts.js';
export const FEE_ASSET_CLASSES = [
    { id: 'CRYPTO', label: 'Crypto' },
    { id: 'STOCK', label: 'Stocks' },
    { id: 'FX', label: 'Forex' },
    { id: 'COMMODITY', label: 'Commodities' },
    { id: 'INDEX', label: 'Indices' },
];
/** instruments.fee_rate must be below this (CHECK constraint). */
export const MAX_TRADING_FEE = '0.1';
/** instruments.fee_flat must be at most this, in USD (CHECK constraint). */
export const MAX_FLAT_FEE = '100';
/**
 * Standard preset: no commission on any class, a flat $1 per trade when it opens (nothing on the
 * close), and no fee on any withdrawal. The rest of the cost is the spread, plus overnight swap on
 * positions held past the swap hour (none on crypto).
 */
export const STANDARD_FEES = {
    trading: { CRYPTO: '0', STOCK: '0', FX: '0', COMMODITY: '0', INDEX: '0' },
    trading_flat: { CRYPTO: '1', STOCK: '1', FX: '1', COMMODITY: '1', INDEX: '1' },
    withdrawal: {
        crypto: { 'USDT-TRC20': '0', 'USDT-ERC20': '0', BTC: '0', ETH: '0' },
        bank_wire: { USD: '0', EUR: '0', GBP: '0' },
        card: { USD: '0', EUR: '0', GBP: '0' },
    },
};
/**
 * What opening a trade costs in fees, for labels: "0.1%", "$1", "0.1% + $1", or "" when it is free.
 * The rate is charged again on the close; the flat fee only on the open.
 */
export function openFeeLabel(feeRate, feeFlat) {
    const parts = [];
    if (D.from(feeRate).isPos())
        parts.push(`${D.from(feeRate).mul(100).toString()}%`);
    if (D.from(feeFlat).isPos())
        parts.push(`$${D.from(feeFlat).toFixed(2).replace(/\.00$/, '')}`);
    return parts.join(' + ');
}
const RATE_RE = /^(0(\.\d{1,6})?|\.\d{1,6})$/;
const METHOD_LABEL = { crypto: 'Crypto', bank_wire: 'Wire transfer', card: 'Card' };
/**
 * Check a change from the admin form or the CRM. Rates are fractions ("0.01" = 1 %); flat trading fees
 * and withdrawal fees are amounts with up to 2 decimals.
 */
export function parseFeeUpdate(raw) {
    const r = (raw ?? {});
    const out = {};
    let changes = 0;
    if (r.trading !== undefined) {
        if (typeof r.trading !== 'object' || r.trading === null)
            return { ok: false, error: 'trading must be an object of asset class → rate' };
        const t = {};
        for (const [k, v] of Object.entries(r.trading)) {
            const cls = FEE_ASSET_CLASSES.find((c) => c.id === k);
            if (!cls)
                return { ok: false, error: `Unknown asset class ${k}; use ${FEE_ASSET_CLASSES.map((c) => c.id).join(', ')}` };
            const s = typeof v === 'number' || typeof v === 'string' ? String(v).trim() : '';
            if (!RATE_RE.test(s) || !D.from(s).lt(MAX_TRADING_FEE))
                return { ok: false, error: `${cls.label}: the fee must be a fraction from 0 to under 0.1, e.g. "0.01" for 1%` };
            t[cls.id] = D.from(s).toString();
            changes++;
        }
        out.trading = t;
    }
    if (r.trading_flat !== undefined) {
        if (typeof r.trading_flat !== 'object' || r.trading_flat === null)
            return { ok: false, error: 'trading_flat must be an object of asset class → USD amount' };
        const t = {};
        for (const [k, v] of Object.entries(r.trading_flat)) {
            const cls = FEE_ASSET_CLASSES.find((c) => c.id === k);
            if (!cls)
                return { ok: false, error: `Unknown asset class ${k}; use ${FEE_ASSET_CLASSES.map((c) => c.id).join(', ')}` };
            const s = typeof v === 'number' || typeof v === 'string' ? String(v).trim() : '';
            if (!MONEY_RE.test(s) || D.from(s).gt(MAX_FLAT_FEE))
                return { ok: false, error: `${cls.label}: the flat fee must be a USD amount from 0 to ${MAX_FLAT_FEE} with up to 2 decimals` };
            t[cls.id] = D.from(s).toString();
            changes++;
        }
        out.trading_flat = t;
    }
    if (r.withdrawal !== undefined) {
        if (typeof r.withdrawal !== 'object' || r.withdrawal === null)
            return { ok: false, error: 'withdrawal must be an object of method → fees' };
        const w = {};
        for (const [m, fees] of Object.entries(r.withdrawal)) {
            if (!(m in METHOD_LABEL))
                return { ok: false, error: `Unknown withdrawal method ${m}; use crypto, bank_wire or card` };
            const method = m;
            if (typeof fees !== 'object' || fees === null)
                return { ok: false, error: `withdrawal.${m} must be an object` };
            const keys = method === 'crypto' ? WITHDRAWAL_ASSETS.map((a) => a.id) : PAYOUT_CURRENCIES;
            const out1 = {};
            for (const [k, v] of Object.entries(fees)) {
                if (!keys.includes(k))
                    return { ok: false, error: `${METHOD_LABEL[method]}: unknown ${method === 'crypto' ? 'asset' : 'currency'} ${k}; use ${keys.join(', ')}` };
                const s = typeof v === 'number' || typeof v === 'string' ? String(v).trim() : '';
                const asset = WITHDRAWAL_ASSETS.find((a) => a.id === k);
                if (asset ? !coinAmountRe(asset.dp).test(s) : !MONEY_RE.test(s)) {
                    return { ok: false, error: `${asset ? asset.label : METHOD_LABEL[method]}: the ${asset ? '' : `${k} `}fee must be an amount in ${asset ? asset.coin : 'USD'} with up to ${asset ? asset.dp : 2} decimals` };
                }
                out1[k] = D.from(s).toString();
                changes++;
            }
            w[method] = out1;
        }
        out.withdrawal = w;
    }
    if (changes === 0)
        return { ok: false, error: 'Nothing to change: send trading, trading_flat and/or withdrawal fees' };
    return { ok: true, update: out };
}
//# sourceMappingURL=fees.js.map